// src/components/CalendarView.tsx
import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Alert, Paper, Grid, Button, Stack, Divider } from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { getMonthCalendar } from '../services/calendarService';
import { MonthCalendar } from '../types/calendar';
import { useAuth } from '../hooks/useAuth';
import CreateEnrichmentDialog from './CreateEnrichmentDialog';
import EnrichmentsView from './EnrichmentsView';
import PostsView from './PostsView';

interface CalendarViewProps {
    initialDay: number;
    initialMonth: number;
    initialYear: number;
}

const CalendarView: React.FC<CalendarViewProps> = ({ initialDay, initialMonth, initialYear }) => {
    const { authState } = useAuth();
    const [monthCalendar, setMonthCalendar] = useState<MonthCalendar | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Date sélectionnée (par défaut la date du jour)
    const [selectedDay, setSelectedDay] = useState(initialDay);
    const [selectedMonth, setSelectedMonth] = useState(initialMonth);
    const [selectedYear, setSelectedYear] = useState(initialYear);

    // Date grégorienne d'aujourd'hui
    const todayGregorian = new Date();
    const todayGregorianDateString = todayGregorian.toISOString().split('T')[0]; // Format YYYY-MM-DD

    // Dialogs state
    const [openEnrichmentDialog, setOpenEnrichmentDialog] = useState(false);

    // Vérifier les permissions
    const userRole = authState.user?.role?.toLowerCase();
    const canCreateOfficialContent = authState.user && (
        userRole === 'superadmin' ||
        userRole === 'admin'
    );

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                // Use initialMonth and initialYear from props
                const calendarRes = await getMonthCalendar(initialYear, initialMonth);
                setMonthCalendar(calendarRes);

            } catch (err) {
                // Error handled by notification system
                setError('Failed to fetch calendar data.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialYear, initialMonth, authState.user?.id]); // Re-fetch when month or year changes

    if (loading) {
        return <CircularProgress />;
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    const handleRefresh = async () => {
        try {
            setLoading(true);
            const calendarRes = await getMonthCalendar(initialYear, initialMonth);
            setMonthCalendar(calendarRes);
        } catch (err) {
            // Error handled by notification system
        } finally {
            setLoading(false);
        }
    };

    const handleDateClick = (day: number) => {
        setSelectedDay(day);
        setSelectedMonth(initialMonth);
        setSelectedYear(initialYear);
    };

    return (
        <Box>
            {/* Header avec boutons de création */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                        <Typography variant="h6">Calendrier Liturgique</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {`Jour: ${initialDay}, Mois: ${initialMonth}, Année: ${initialYear}`}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        {canCreateOfficialContent && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AdminPanelSettingsIcon />}
                                onClick={() => setOpenEnrichmentDialog(true)}
                            >
                                Créer Enrichissement
                            </Button>
                        )}
                    </Stack>
                </Stack>

                {/* Info sur les permissions */}
                {canCreateOfficialContent && (
                    <Alert severity="info" icon={<AdminPanelSettingsIcon />} sx={{ mb: 2 }}>
                        En tant qu'<strong>{authState.user?.role}</strong>, vous pouvez créer des enrichissements officiels visibles par tous les utilisateurs.
                    </Alert>
                )}
            </Paper>

            {/* Calendrier mensuel */}
            {monthCalendar && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        {monthCalendar.month_name} {monthCalendar.year}
                    </Typography>
                    <Grid container spacing={1}>
                        {monthCalendar.days.map(day => {
                            const isSelected = day.day === selectedDay;
                            // Vérifier si c'est aujourd'hui en comparant les dates grégoriennes
                            const dayGregorianDateString = day.gregorian_date.split('T')[0]; // Format YYYY-MM-DD
                            const isTodayGregorian = dayGregorianDateString === todayGregorianDateString;

                            return (
                                <Grid item xs={1.7} key={day.day}>
                                    <Paper
                                        onClick={() => handleDateClick(day.day)}
                                        sx={{
                                            p: 1,
                                            textAlign: 'center',
                                            backgroundColor: isSelected
                                                ? 'primary.main'
                                                : isTodayGregorian
                                                    ? 'success.light'
                                                    : 'inherit',
                                            color: isSelected ? 'white' : 'inherit',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            border: isTodayGregorian && !isSelected ? '2px solid' : 'none',
                                            borderColor: isTodayGregorian ? 'success.main' : 'transparent',
                                            '&:hover': {
                                                boxShadow: 4,
                                                transform: 'scale(1.05)'
                                            }
                                        }}
                                    >
                                        <Typography variant="body2" fontWeight={isSelected || isTodayGregorian ? 'bold' : 'normal'}>
                                            {day.day}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color={isSelected ? 'inherit' : 'text.secondary'}
                                        >
                                            {new Date(day.gregorian_date).getDate()}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Paper>
            )}

            {/* Divider pour séparer le calendrier du contenu */}
            <Divider sx={{ my: 3 }} />

            {/* Afficher la date sélectionnée */}
            <Typography variant="h5" gutterBottom sx={{ mt: 3, mb: 2 }}>
                Contenu du {selectedDay}/{selectedMonth}/{selectedYear}
            </Typography>

            {/* Enrichissements du jour */}
            <Box sx={{ mb: 4 }}>
                <EnrichmentsView day={selectedDay} month={selectedMonth} />
            </Box>

            {/* Posts du jour */}
            <Box sx={{ mb: 4 }}>
                <PostsView day={selectedDay} month={selectedMonth} />
            </Box>

            {/* Dialogs */}
            {canCreateOfficialContent && (
                <CreateEnrichmentDialog
                    open={openEnrichmentDialog}
                    onClose={() => setOpenEnrichmentDialog(false)}
                    selectedDate={{ day: selectedDay, month: selectedMonth, year: selectedYear }}
                    onSuccess={handleRefresh}
                />
            )}
        </Box>
    );
};

export default CalendarView;
